import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLearner, useUpdateLearner, useDeleteLearner } from '../../services/learners';
import { uploadFile, useImageUrl } from '../../services/uploads';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { phantomProps } from '@/config/phantom';
import DepartmentSelect from '../common/DepartmentSelect';
import RoleSelect from '../common/RoleSelect';
import LocationSelect from '../common/LocationSelect';

const sanitize = (v: string) => DOMPurify.sanitize(v, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

const schema = z.object({
  full_name: z
    .string()
    .min(2, 'Full name is required')
    .max(200, 'Name must be 200 characters or less')
    .refine(v => v.trim().includes(' '), 'Please enter both first and last name'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  department_id: z.string().optional(),
  role_id: z.string().optional(),
  location_id: z.string().optional(),
});

type FormData = z.infer<typeof schema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

interface LearnerEditModalProps {
  learnerId: string;
  onClose: () => void;
  onSaved?: () => void;
  onDeleted?: () => void;
}

const FloatingInput: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  name?: string;
}> = ({ id, label, value, onChange, disabled, error, name }) => (
  <div className="relative w-full">
    <input
      id={id}
      name={name ?? id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder=" "
      className={`peer w-full px-3.5 pt-5 pb-3 border rounded-lg text-sm text-text-primary bg-background-primary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${error ? 'border-error-primary' : 'border-[rgba(189,188,201,0.4)]'}`}
    />
    <label
      htmlFor={id}
      className="absolute left-3.5 top-0 -translate-y-1/2 text-xs font-medium text-text-secondary bg-background-primary px-1 pointer-events-none transition-[top,font-size,font-weight] duration-150
        peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal
        peer-focus:top-0 peer-focus:text-xs peer-focus:font-medium rounded-md"
    >
      {label}
    </label>
    {error && <p className="mt-1 text-xs text-error-700">{error}</p>}
  </div>
);

const LearnerEditModal: React.FC<LearnerEditModalProps> = ({
  learnerId,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const { activeMembership } = useAuth();
  const { theme } = useTheme();
  const userRole = activeMembership?.role;
  const canDelete = userRole === 'Admin' || userRole === 'Owner';

  const { data: learner, isLoading } = useLearner(learnerId);
  const updateMutation = useUpdateLearner();
  const deleteMutation = useDeleteLearner();

  const [form, setForm] = useState<FormData>({
    full_name: '',
    email: '',
    department_id: undefined,
    role_id: undefined,
    location_id: undefined,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Avatar upload state
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [pendingAvatarKey, setPendingAvatarKey] = useState<string | null>(null);

  // Resolve existing avatar (S3 key or full URL) to a displayable URL
  const [existingAvatarUrl] = useImageUrl(!avatarPreview ? (learner?.avatar_url ?? null) : null);

  useEffect(() => {
    if (learner) {
      setForm({
        full_name: `${learner.first_name} ${learner.last_name}`.trim(),
        email: learner.email ?? '',
        department_id: learner.department_id ?? undefined,
        role_id: learner.role_id ?? undefined,
        location_id: learner.location_id ?? undefined,
      });
      setIsDirty(false);
    }
  }, [learner]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: sanitize(value) }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
    setIsDirty(true);
  };

  const handleAvatarSelect = async (file: File) => {
    const MAX = 5 * 1024 * 1024;
    const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
    if (file.size > MAX) { toast.error('Image must be under 5 MB'); return; }
    if (!TYPES.includes(file.type)) { toast.error('Only JPG, PNG, or WebP allowed'); return; }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
    setAvatarFile(file);

    try {
      setIsUploadingAvatar(true);
      const result = await uploadFile({ file, resource_type: 'learner_avatar', resource_id: learnerId });
      setPendingAvatarKey(result.key);
      setIsDirty(true);
    } catch {
      toast.error('Avatar upload failed. Please try again.');
      setAvatarPreview(null);
      setAvatarFile(null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const validate = (): boolean => {
    const result = schema.safeParse(form);
    if (result.success) { setErrors({}); return true; }
    const errs: FieldErrors = {};
    result.error.issues.forEach(issue => {
      errs[issue.path[0] as keyof FormData] = issue.message;
    });
    setErrors(errs);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const parts = form.full_name.trim().split(' ');
    const first_name = parts[0];
    const last_name = parts.slice(1).join(' ');

    try {
      await updateMutation.mutateAsync({
        learnerId,
        data: {
          first_name,
          last_name,
          email: form.email.trim().toLowerCase(),
          ...(form.department_id !== undefined ? { department_id: form.department_id } : {}),
          ...(form.role_id !== undefined ? { role_id: form.role_id } : {}),
          ...(form.location_id !== undefined ? { location_id: form.location_id } : {}),
          ...(pendingAvatarKey ? { avatar_url: pendingAvatarKey } : {}),
        },
      });
      toast.success('Learner updated successfully');
      setIsDirty(false);
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update learner. Please try again.';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(learnerId);
      toast.success('Learner deleted successfully');
      onDeleted?.();
      onClose();
    } catch {
      toast.error('Failed to delete learner. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const isBusy = updateMutation.isPending || deleteMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 bg-[rgba(11,11,11,0.7)]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Dialog panel */}
      <div className="relative bg-surface-primary rounded-t-2xl md:rounded-2xl w-full md:max-w-[922px] overflow-hidden shadow-[0px_0px_90px_0px_rgb(var(--color-primary) / 0.35)] max-h-[92dvh] md:max-h-[90dvh] flex flex-col">

        {/* Gradient background overlay */}
        <div
          aria-hidden="true"
          className="brand-gradient absolute inset-0 opacity-40 dark:opacity-10 pointer-events-none select-none rounded-t-2xl md:rounded-2xl"
        />

        {/* Scrollable content wrapper */}
        <div className="relative flex flex-col overflow-y-auto">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
            <h2 className="text-[17px] md:text-lg font-semibold text-text-primary leading-tight">
              Update Learner
            </h2>
            {canDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isBusy}
                className="px-3 py-1.5 rounded-full bg-secondary-500 text-sm font-semibold hover:bg-secondary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                Delete user
              </button>
            )}
          </div>

          {/* ── Form body ── */}
          <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
            <form id="learner-edit-form" onSubmit={handleSubmit} noValidate>
              <div className="px-6 space-y-4">
                {/* Avatar upload */}
                <div className="flex items-center gap-4">
                  <div className="relative flex-shrink-0">
                    {/* Avatar circle */}
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-background-tertiary border border-border-primary">
                      {(avatarPreview || existingAvatarUrl) ? (
                        <img
                          src={avatarPreview ?? existingAvatarUrl ?? undefined}
                          alt="Avatar"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xl font-semibold text-text-secondary">
                          {learner ? `${learner.first_name[0]}${learner.last_name[0]}`.toUpperCase() : '??'}
                        </div>
                      )}
                    </div>
                    {/* Upload spinner overlay */}
                    {isUploadingAvatar && (
                      <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="px-3 py-1.5 text-sm font-medium border border-border-primary rounded-full hover:bg-background-secondary transition-colors disabled:opacity-50 w-fit"
                    >
                      {avatarFile ? 'Change photo' : 'Upload photo'}
                    </button>
                    {avatarFile && (
                      <button
                        type="button"
                        onClick={() => { setAvatarFile(null); setAvatarPreview(null); setPendingAvatarKey(null); }}
                        className="text-xs text-text-secondary hover:text-error-600 transition-colors text-left"
                      >
                        Remove
                      </button>
                    )}
                    <p className="text-xs text-text-secondary">JPG, PNG or WebP · max 5 MB</p>
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarSelect(f); e.target.value = ''; }}
                  />
                </div>
                {/* Alert: unsaved changes */}
                {isDirty && (
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[rgb(var(--color-secondary) / 0.15)] border border-[rgb(var(--color-secondary) / 0.35)]">
                    <InformationCircleIcon className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                    <span className="text-sm text-secondary-500 flex-1 leading-snug">
                      Change is waiting for confirmation
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsDirty(false)}
                      className="p-1 rounded hover:bg-[rgb(var(--color-secondary) / 0.15)] transition-colors flex-shrink-0"
                    >
                      <XMarkIcon className="w-4 h-4 text-secondary-500" />
                    </button>
                  </div>
                )}

                {/* Row 1: Full name + Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FloatingInput
                    id="full_name"
                    label="Full name"
                    value={form.full_name}
                    onChange={handleChange}
                    error={errors.full_name}
                  />
                  <FloatingInput
                    id="email"
                    label="Email address"
                    value={form.email}
                    onChange={handleChange}
                    error={errors.email}
                  />
                </div>

                {/* Row 2: Department + Role */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DepartmentSelect
                    value={form.department_id ?? ''}
                    onChange={(val) => {
                      setForm(prev => ({ ...prev, department_id: val || undefined }));
                      setIsDirty(true);
                    }}
                  />
                  <RoleSelect
                    value={form.role_id ?? ''}
                    onChange={(val) => {
                      setForm(prev => ({ ...prev, role_id: val || undefined }));
                      setIsDirty(true);
                    }}
                  />
                </div>

                {/* Row 3: Location (full width) */}
                <LocationSelect
                  label="Add Location"
                  value={form.location_id ?? ''}
                  onChange={(val) => {
                    setForm(prev => ({ ...prev, location_id: val || undefined }));
                    setIsDirty(true);
                  }}
                />
              </div>

              {/* ── Footer ── */}
              <div className="flex items-center justify-center md:justify-end gap-3 px-6 py-5 mt-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isBusy}
                  className="flex-1 md:flex-none px-5 py-2 rounded-full border border-[rgba(189,188,201,0.5)] text-sm font-semibold text-text-primary hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="learner-edit-form"
                  disabled={isBusy}
                  className="flex-1 md:flex-none px-5 py-2 rounded-full bg-primary-600 dark:bg-primary-350 text-sm font-semibold hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </phantom-ui>
        </div>
      </div>

      {/* ── Delete confirmation ── */}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.45)]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteConfirm(false); }}
        >
          <div className="bg-surface-primary rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-base font-bold text-text-primary mb-2">Delete Learner</h3>
            <p className="text-sm text-text-secondary mb-6 leading-relaxed">
              Are you sure you want to delete{' '}
              <strong className="text-text-primary">
                {learner?.first_name} {learner?.last_name}
              </strong>
              ? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium border border-[rgba(189,188,201,0.4)] rounded-full hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-full bg-error-600 text-white text-sm font-semibold hover:bg-error-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearnerEditModal;
